import { type App, readonly, ref } from 'vue'
import type { Router } from 'vue-router'
import TitleBar from './TitleBar.vue'
import { invoke } from '@kiti/js-bridge'

export default {
  install(
    app: App,
    options: {
      router: Router
    }
  ) {
    const documentRootEle = document.documentElement
    const curTheme = ref(documentRootEle.getAttribute('data-theme-real') || 'light')

    app.config.globalProperties.$pageTheme = {
      curTheme: readonly(curTheme),
      toggleTheme(themeName?: string) {
        if (themeName) {
          curTheme.value = themeName
        } else {
          curTheme.value = curTheme.value === 'light' ? 'dark' : 'light'
        }
        documentRootEle.setAttribute('data-theme', curTheme.value)
      },
      resetAutoTheme() {
        const curRealTheme = documentRootEle.getAttribute('data-theme-real') || 'light'

        curTheme.value = curRealTheme
        documentRootEle.setAttribute('data-theme', curRealTheme)
      },
      getThemeVariable(keyName: string) {
        keyName = keyName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
        const computedStyle = getComputedStyle(documentRootEle)
        const res = computedStyle.getPropertyValue(`--${keyName}`).trim()

        return res
      }
    }
    app.provide('pageTheme', curTheme)
    const pageLayoutInfo = ref<null | {
      statusBar: {
        height: string
      }
      titleBar: {
        height: string
      }
      twoBar: {
        height: string
      }
    }>(null)

    app.provide('layoutInfo', pageLayoutInfo)
    // 如果变了皮肤，就自动切换皮肤模式。
    const mutationObserver = new MutationObserver(mutationList => {
      for (const mutation of mutationList) {
        // 如果系统模式变了，要看当前路由的情况决定是否真的变。
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme-real') {
          const currentRouteMode = (
            options.router.currentRoute.value.meta.layout as { theme?: string }
          )?.theme

          if (currentRouteMode === 'auto') {
            app.config.globalProperties.$pageTheme.resetAutoTheme()
          }
        }
      }
    })

    mutationObserver.observe(documentRootEle, {
      attributes: true,
      attributeFilter: ['data-theme-real']
    })

    // 路由钩子配置
    options.router.afterEach(function (to) {
      window.setTitleBarOptions(null)
      const layout = (to.meta.layout as {
        theme: string
        titleBar: {
          center: string
        }
      }) || { theme: '', titleBar: { center: '' } }

      layout.theme = layout.theme || 'light'
      if (layout.theme !== 'auto') {
        app.config.globalProperties.$pageTheme.toggleTheme(layout.theme)
      } else {
        app.config.globalProperties.$pageTheme.resetAutoTheme()
      }
      // 再去配置上路由标题
      if (typeof layout?.titleBar?.center === 'string') {
        document.title = layout.titleBar.center
      }
    })

    // 重写back和go。
    const router = options.router
    const routerOldGo = router.go

    function historyGo(step: number) {
      invoke('historyGo', {
        step: -1
      }).catch(() => {
        routerOldGo(step)
      })
    }
    router.back = () => {
      historyGo(-1)
    }
    router.go = (step: number) => {
      historyGo(step)
    }

    // 注册拥有皮肤跟随能力的TitleBar组件
    app.component('TitleBar', TitleBar)
  }
}
